// @ts-check
/**
 * **印の語彙**（Issue #534。索引 #525。`duelEditor/core/` ＝ 舞台の決まりごと）。
 *
 * 印 ＝ **演出がオブジェクトレイヤーに置く絵**。帯が終わっても残り、**別の演出が消す**
 * （設計文書 §4）。だから**綴りを 2 つの演出ファイルが手で合わせる**ことになり、
 * 旧 `/fx` が HUD の箱の直読みで 3 回壊した（#507〜#509）のと同じ形になる。
 *
 * それを避けるために**語彙をここに 1 枚置く**: 演出が渡すのは `{ mark, key }` だけで、
 * **id は器が組む**。知らない `mark` は例外（`toTheme` の「知らない層の名前は弾く」と同じ流儀）。
 *
 * ## 情報は `history`、絵はここ
 *
 * 次の演出に伝えるためだけの**見えない印は置かない**（設計文書 §4.2）。
 * またぐ情報は `history`（過去だけを引く口）から読む。
 */

/**
 * **置ける印の一覧**。足すときはここに 1 行。
 *
 * `space` は `core/theme` の `OBJECT_KINDS` と同じ語彙で、
 * `board` ＝ 盤の上（遠近が付く）/ `screen` ＝ 舞台のピクセル（付かない）。
 */
export const MARKS = Object.freeze([
  { name: 'target', label: '対象の印', space: 'board' },
  /*
   * 対象の照準の枠（四隅の括弧。同じカードに複数のリンクがあれば入れ子。Issue #571）。
   * 角にチェーンの番号を正立で添えるので舞台のピクセル（盤の上だと実写の横倒しの盤で文字が横を向く）
   */
  { name: 'targetSight', label: '対象の照準の枠', space: 'screen' },
  { name: 'chainCard', label: 'チェーンの札', space: 'screen' },
  { name: 'material', label: '召喚素材', space: 'board' },
  { name: 'processing', label: '処理中の札', space: 'screen' },
  // 攻撃の矢印（攻撃宣言で置き、ダメージステップの終わり・フェイズ・ターンの切り替えで消す。Issue #552）
  { name: 'attackArrow', label: '攻撃の矢印', space: 'screen' },
  // 対戦の小さいパネル（攻撃宣言の大きいパネルが縮んで残る。消えるのは攻撃の矢印と同じ。Issue #552）
  { name: 'battlePanel', label: '対戦の小さいパネル', space: 'screen' },
].map((m) => Object.freeze(m)));

export const MARK_NAMES = Object.freeze(MARKS.map((m) => m.name));

const BY_NAME = new Map(MARKS.map((m) => [m.name, m]));

/** その名前の印（**知らない名前は例外**。綴り間違いを黙って「何も置かない」にしない） */
export function markOf(name, where = 'mark') {
  const hit = BY_NAME.get(String(name ?? ''));
  if (!hit) throw new Error(`${where}: 知らない印 ${name}（${MARK_NAMES.join(' / ')}）`);
  return hit;
}

/**
 * 置かれたものの id。**器が組む**ので、演出は綴りを知らなくてよい。
 * @param {string} mark
 * @param {string|number|null} [key] 同じ印の中で一意（たいていカードの uid）
 */
export const markId = (mark, key = null) => `fx:${mark}${key == null ? '' : `:${key}`}`;

/**
 * **置く / 消すの宣言を検める**（`defineEffect` が `places` / `clears` の返り値に使う）。
 * 知らない印は例外にする。
 */
export function checkPlacements(list, where = 'places') {
  (list ?? []).forEach((p, i) => {
    if (!p || typeof p !== 'object') throw new Error(`${where}[${i}]: object です`);
    markOf(p.mark, `${where}[${i}]`);
  });
  return list ?? [];
}
